'use client';

import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useTranslations } from 'next-intl';
import type {
  BodyScrollEvent,
  CellContextMenuEvent,
  ColDef,
  GetRowIdParams,
  GridApi,
  GridReadyEvent,
  ICellRendererParams,
  IsFullWidthRowParams,
  RowClassParams,
  RowClickedEvent,
  RowHeightParams,
  RowStyle,
  SortChangedEvent,
} from 'ag-grid-community';
import { Box, HStack } from '@terra/ui-styled-system';
import {
  GroupHeaderCell,
  MetaDefault,
  Spinner,
  TABLE_IDS,
  Table,
  archivedEntityRowCss,
  isArchivedLifecycle,
} from '@terra/ui-design-system';
import { Shield01Icon } from '@terra/ui-icons';
import type { GroupedSignal } from '@terra/shared-contracts-signals';
import { useDomScrollPreservation } from '../../../hooks/useDomScrollPreservation';
import { EmptyState } from '../../EmptyState';
import {
  LIST_PAGE_TABLE_HEADER_CSS,
  LIST_PAGE_TABLE_HEADER_HEIGHT_PX,
  LIST_PAGE_TABLE_THEME_PARAMS,
} from '../../common/list-page-table-theme';
import { shouldLoadMoreListPageRows } from '../../common/list-page-infinite-table.model';
import { ListPageTableBodyLayer } from '../../common/ListPageTableBodyLayer';
import { useListPageTablePersistence } from '../../common/useListPageTablePersistence';
import { isSignalDataRow, isSignalDetailRow } from './signals-list.model';
import {
  SIGNALS_GROUP_HEADER_ROW_CLASS,
  isSameSignalsSortState,
  isSignalsGroupHeaderRow,
  resolveSignalsColumnSortState,
  resolveSignalsSortChange,
  type SignalsSortState,
  type SignalsTableRow,
} from './signals-grouping.model';
import {
  SIGNAL_ROW_DETAIL_HEIGHT_PX,
  SignalRowDetail,
} from './SignalRowDetail';
import { useSignalsTableColumnDefs } from './useSignalsTableColumnDefs';
import {
  isRowClickDisabledTarget,
  rowClickBlockerProps,
} from '../rowClickBlocker';

const SIGNALS_TABLE_ROW_HEIGHT_PX = 36;
const USER_SORT_SOURCE = 'uiColumnSorted';
const DEFAULT_COL_DEF: ColDef<SignalsTableRow> = {
  sortable: false,
  filter: false,
  resizable: true,
  lockPinned: true,
  sortingOrder: ['asc', 'desc'],
};
const SIGNALS_TABLE_CSS = {
  ...LIST_PAGE_TABLE_HEADER_CSS,
  [`& .${SIGNALS_GROUP_HEADER_ROW_CLASS}`]: {
    backgroundColor: 'var(--colors-bg-subtle)',
  },
};

type SignalsTableProps = {
  rows: SignalsTableRow[];
  signalCount: number;
  loadMoreRowIndex: number | null;
  scrollId: string;
  sortState: SignalsSortState;
  canSort: boolean;
  isRejectedView: boolean;
  showSourceColumn: boolean;
  showAppWideColumn: boolean;
  isLoading: boolean;
  isFetchingMore: boolean;
  hasMore: boolean;
  hasActiveFilters: boolean;
  onLoadMore: () => void;
  onSortStateChange: (sortState: SignalsSortState) => void;
  onToggleGroup: (groupKey: string) => void;
  onOpenSignal: (signal: GroupedSignal) => void;
  onOpenSignalContextMenu: ({
    event,
    signal,
  }: {
    event: MouseEvent;
    signal: GroupedSignal;
  }) => void;
};

const getSignalsTableRowId = (params: GetRowIdParams<SignalsTableRow>) =>
  params.data.id;

const isSignalsFullWidthRow = (params: IsFullWidthRowParams<SignalsTableRow>) =>
  isSignalDetailRow(params.rowNode.data) ||
  isSignalsGroupHeaderRow(params.rowNode.data);

const getSignalsTableRowHeight = (params: RowHeightParams<SignalsTableRow>) =>
  isSignalDetailRow(params.data)
    ? SIGNAL_ROW_DETAIL_HEIGHT_PX
    : SIGNALS_TABLE_ROW_HEIGHT_PX;

const getSignalsTableRowStyle = (
  params: RowClassParams<SignalsTableRow>,
): RowStyle | undefined => {
  if (!isSignalDataRow(params.data)) {
    return { cursor: 'default' };
  }

  return {
    cursor: 'pointer',
    ...(isArchivedLifecycle(params.data.signal.lifecycle)
      ? archivedEntityRowCss
      : {}),
  };
};

const getSignalsTableRowClass = (
  params: RowClassParams<SignalsTableRow>,
): string | undefined =>
  isSignalsGroupHeaderRow(params.data)
    ? SIGNALS_GROUP_HEADER_ROW_CLASS
    : undefined;

function SignalsTableEmptyState({
  hasActiveFilters,
}: Pick<SignalsTableProps, 'hasActiveFilters'>) {
  const t = useTranslations('proxy.signals.table');

  return (
    <EmptyState
      icon={Shield01Icon}
      title={t('emptyTitle')}
      description={
        hasActiveFilters ? t('emptyFilteredDescription') : t('emptyDescription')
      }
    />
  );
}

export function SignalsTable({
  rows,
  signalCount,
  loadMoreRowIndex,
  scrollId,
  sortState,
  canSort,
  isRejectedView,
  showSourceColumn,
  showAppWideColumn,
  isLoading,
  isFetchingMore,
  hasMore,
  hasActiveFilters,
  onLoadMore,
  onSortStateChange,
  onToggleGroup,
  onOpenSignal,
  onOpenSignalContextMenu,
}: SignalsTableProps) {
  const t = useTranslations('proxy.signals.table');
  const columnDefs = useSignalsTableColumnDefs({
    isRejectedView,
    showSourceColumn,
    showAppWideColumn,
    canSort,
  });
  const persistence = useListPageTablePersistence(
    isRejectedView
      ? TABLE_IDS.PROXY_REJECTED_SIGNALS_LIST
      : TABLE_IDS.PROXY_SIGNALS_LIST,
  );
  const containerRef = useRef<HTMLDivElement | null>(null);
  const gridApiRef = useRef<GridApi<SignalsTableRow> | null>(null);
  const { scrollerRef } = useDomScrollPreservation({
    scrollId,
    isRestoreReady: !isLoading && rows.length > 0,
    contentRevision: rows.length,
  });
  const columnSortState = useMemo(
    () => (canSort ? resolveSignalsColumnSortState(sortState) : []),
    [canSort, sortState],
  );
  const applyColumnSortState = useCallback(
    (api: GridApi<SignalsTableRow>) =>
      api.applyColumnState({
        state: columnSortState,
        defaultState: { sort: null, sortIndex: null },
      }),
    [columnSortState],
  );
  const loadMoreIfNearEnd = useCallback(
    (api: GridApi<SignalsTableRow>) => {
      if (
        shouldLoadMoreListPageRows({
          lastDisplayedRowIndex: api.getLastDisplayedRowIndex(),
          loadMoreRowIndex,
          hasMore,
          isFetchingMore,
        })
      ) {
        onLoadMore();
      }
    },
    [hasMore, isFetchingMore, loadMoreRowIndex, onLoadMore],
  );

  useEffect(() => {
    if (gridApiRef.current && !gridApiRef.current.isDestroyed()) {
      applyColumnSortState(gridApiRef.current);
    }
  }, [applyColumnSortState]);

  const handleGridReady = useCallback(
    (event: GridReadyEvent<SignalsTableRow>) => {
      gridApiRef.current = event.api;
      applyColumnSortState(event.api);
      scrollerRef(
        containerRef.current?.querySelector<HTMLElement>('.ag-body-viewport') ??
          null,
      );
    },
    [applyColumnSortState, scrollerRef],
  );
  const handleSortChanged = useCallback(
    (event: SortChangedEvent<SignalsTableRow>) => {
      if (event.source !== USER_SORT_SOURCE) {
        return;
      }

      const sortedColumn = event.api
        .getColumnState()
        .find((column) => column.sort === 'asc' || column.sort === 'desc');
      const change = resolveSignalsSortChange({
        ...sortState,
        sortedColumn: sortedColumn
          ? {
              colId: sortedColumn.colId,
              sort: sortedColumn.sort === 'asc' ? 'asc' : 'desc',
            }
          : null,
      });

      if (isSameSignalsSortState({ left: change, right: sortState })) {
        applyColumnSortState(event.api);

        return;
      }

      onSortStateChange(change);
    },
    [applyColumnSortState, onSortStateChange, sortState],
  );
  const renderFullWidthRow = useCallback(
    (params: ICellRendererParams<SignalsTableRow>) => {
      if (isSignalsGroupHeaderRow(params.data)) {
        return (
          <Box h="full" px={3}>
            <GroupHeaderCell
              group={params.data}
              isCollapsed={params.data.isCollapsed}
              onToggle={onToggleGroup}
              containerProps={rowClickBlockerProps}
            />
          </Box>
        );
      }

      return isSignalDetailRow(params.data) ? (
        <SignalRowDetail signal={params.data.signal} />
      ) : null;
    },
    [onToggleGroup],
  );
  const handleRowClicked = useCallback(
    (event: RowClickedEvent<SignalsTableRow>) => {
      if (
        !isSignalDataRow(event.data) ||
        isRowClickDisabledTarget(event.event)
      ) {
        return;
      }

      onOpenSignal(event.data.signal);
    },
    [onOpenSignal],
  );
  const handleCellContextMenu = useCallback(
    (event: CellContextMenuEvent<SignalsTableRow>) => {
      if (
        !isSignalDataRow(event.data) ||
        !(event.event instanceof MouseEvent)
      ) {
        return;
      }

      onOpenSignalContextMenu({
        event: event.event,
        signal: event.data.signal,
      });
    },
    [onOpenSignalContextMenu],
  );

  return (
    <Box
      ref={containerRef}
      flex={1}
      w="full"
      h="full"
      overflow="hidden"
      position="relative"
      css={SIGNALS_TABLE_CSS}
    >
      <Table
        key={isRejectedView ? 'rejected' : 'signals'}
        columnDefs={columnDefs}
        persistence={persistence}
        rowData={rows}
        getRowId={getSignalsTableRowId}
        rowHeight={SIGNALS_TABLE_ROW_HEIGHT_PX}
        getRowHeight={getSignalsTableRowHeight}
        isFullWidthRow={isSignalsFullWidthRow}
        fullWidthCellRenderer={renderFullWidthRow}
        headerHeight={LIST_PAGE_TABLE_HEADER_HEIGHT_PX}
        noBorder={true}
        fontSize={12}
        headerFontSize={12}
        themeParams={LIST_PAGE_TABLE_THEME_PARAMS}
        animateRows={false}
        defaultColDef={DEFAULT_COL_DEF}
        suppressCellFocus={true}
        suppressMultiSort={true}
        hideColumnControls={true}
        fitColumnsOnGridSizeChanged={false}
        onGridReady={handleGridReady}
        onSortChanged={handleSortChanged}
        onBodyScroll={(event: BodyScrollEvent<SignalsTableRow>) =>
          loadMoreIfNearEnd(event.api)
        }
        onRowDataUpdated={(event) => loadMoreIfNearEnd(event.api)}
        onRowClicked={handleRowClicked}
        onCellContextMenu={handleCellContextMenu}
        preventDefaultOnContextMenu
        getRowStyle={getSignalsTableRowStyle}
        getRowClass={getSignalsTableRowClass}
        suppressNoRowsOverlay
      />
      {signalCount === 0 && !isLoading ? (
        <ListPageTableBodyLayer>
          <SignalsTableEmptyState hasActiveFilters={hasActiveFilters} />
        </ListPageTableBodyLayer>
      ) : null}
      {isFetchingMore ? (
        <HStack
          position="absolute"
          bottom={3}
          left="50%"
          transform="translateX(-50%)"
          gap={2}
          px={3}
          py={1.5}
          borderRadius="full"
          bg="bg.default"
          boxShadow="sm"
          pointerEvents="none"
        >
          <Spinner size="sm" />
          <MetaDefault color="fg.muted">{t('loadingMore')}</MetaDefault>
        </HStack>
      ) : null}
    </Box>
  );
}
